// The Books menu: one list, grouped like an accounting app. AppShell renders it
// in the left sidebar while you are inside /books; BooksHub reads the labels.
import type { ReactNode } from 'react';

export type BooksTab =
  | 'overview' | 'apple' | 'income' | 'spending' | 'vendors' | 'banking'
  | 'assets' | 'members' | 'journal' | 'reports' | 'periods' | 'settings' | 'access';

const I = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);

export const BOOKS_ICONS: Record<BooksTab, ReactNode> = {
  overview: I(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>),
  apple: I(<><path d="M12 7c-1.5-2-5-2-6 1-1.2 3.5 1 9 3 10 1 .5 2-.3 3-.3s2 .8 3 .3c2-1 4.2-6.5 3-10-1-3-4.5-3-6-1z" /><path d="M12 7c0-2 1-3.5 3-4" /></>),
  income: I(<><path d="M12 20V4" /><path d="M6 10l6-6 6 6" /><path d="M4 20h16" /></>),
  spending: I(<><path d="M12 4v16" /><path d="M6 14l6 6 6-6" /><path d="M4 4h16" /></>),
  vendors: I(<><path d="M4 9l1.5-5h13L20 9" /><path d="M4 9h16v2a3 3 0 0 1-5.3 2 3 3 0 0 1-5.4 0A3 3 0 0 1 4 11z" /><path d="M5 13v7h14v-7" /></>),
  banking: I(<><path d="M3 9l9-5 9 5" /><path d="M5 9v9M10 9v9M14 9v9M19 9v9" /><path d="M3 20h18" /></>),
  assets: I(<><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>),
  members: I(<><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M15 14.5c3 0 6 1.8 6 5.5" /></>),
  journal: I(<><path d="M5 4h11l3 3v13H5z" /><path d="M9 10h6M9 14h6M9 18h4" /></>),
  reports: I(<><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>),
  periods: I(<><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /><path d="M9 15l2 2 4-4" /></>),
  settings: I(<><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" /></>),
  access: I(<><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>),
};

export type BooksNavItem = { key: BooksTab; label: string; title: string; blurb: string };

export const BOOKS_NAV: { group: string | null; items: BooksNavItem[] }[] = [
  { group: null, items: [
    { key: 'overview', label: 'Dashboard', title: 'Dashboard', blurb: 'Where the money stands today.' },
  ] },
  { group: 'Money in', items: [
    { key: 'apple', label: 'Apple sales', title: 'Apple sales', blurb: 'Academy proceeds, shared costs, and the member split, one month at a time.' },
    { key: 'income', label: 'Other income', title: 'Other income', blurb: 'Workshops, courses, and anything that does not come through Apple.' },
  ] },
  { group: 'Money out', items: [
    { key: 'spending', label: 'Expenses', title: 'Expenses', blurb: 'Bills and purchases, by account.' },
    { key: 'vendors', label: 'Vendors', title: 'Vendors', blurb: 'Who Rootegra pays and how each bill is split.' },
  ] },
  { group: 'Cash', items: [
    { key: 'banking', label: 'Banking', title: 'Banking', blurb: 'Accounts, statement imports, review, and reconciliation.' },
  ] },
  { group: 'Company', items: [
    { key: 'assets', label: 'Fixed assets', title: 'Fixed assets', blurb: 'Equipment and depreciation.' },
    { key: 'members', label: 'Members', title: 'Members', blurb: 'Capital accounts, contributions, and distributions.' },
  ] },
  { group: 'Accounting', items: [
    { key: 'journal', label: 'Journal', title: 'Journal entries', blurb: 'Every entry in the books.' },
    { key: 'reports', label: 'Reports', title: 'Reports', blurb: 'Financial statements and the Form 1065 summary.' },
    { key: 'periods', label: 'Month end', title: 'Month end', blurb: 'Close and reopen months.' },
  ] },
  { group: 'Admin', items: [
    { key: 'settings', label: 'Settings', title: 'Settings', blurb: 'Chart of accounts, entity details, and account map.' },
    { key: 'access', label: 'Access', title: 'Access and log', blurb: 'Who can view or edit, and every change made.' },
  ] },
];

export const BOOKS_ITEMS: BooksNavItem[] = BOOKS_NAV.flatMap((g) => g.items);
export const booksPath = (t: BooksTab) => (t === 'overview' ? '/books' : `/books/${t}`);
